import { useContext } from "react";
import { GlobalContext } from "../util/globalContext";

export function useOrganizationApi() {
const { config, apiFetch } = useContext(GlobalContext);
const BASE_URL = config?.REACT_APP_BASE_URL || "http://localhost:3000"
// ✅ Utility to get headers with token
function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    "X-token": token || "",
  };
}

// ✅ Get all vendor organizations with pagination, search, and filters
async function getAllVendorOrg({ limit = 5, offset = 0, search = "", ...filters } = {}) {
  const body = {
    limit,
    offset,
    ...(search ? { search } : {}),
    ...filters, // include other filter fields if provided
  };

  const response = await apiFetch(`${BASE_URL}/get_all_vendor_org`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.message || "Failed to fetch organizations");
  }

  return data;
}

// ✅ Create a vendor organization
async function createVendorOrg(orgData) {
  const response = await apiFetch(`${BASE_URL}/create_vendor_org`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(orgData),
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || "Failed to create organization");
  }

  return data;
}

// ✅ Delete vendor organization
async function deleteVendorOrg(_id) {
  const response = await apiFetch(`${BASE_URL}/delete_vendor_org/${_id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || "Failed to delete organization");
  }

  return data;
}

// ✅ Get organization by ID
async function getVendorOrgById(id, requestOptions = {}) {
  const response = await apiFetch(`${BASE_URL}/get_vendor_org/${id}`, {
    method: "GET",
    headers: getAuthHeaders(),
  }, requestOptions);

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || "Failed to fetch organization");
  }

  return data.data;
}

// ✅ Update organization
async function updateVendorOrg(id, updateData) {
  const response = await apiFetch(`${BASE_URL}/update_vendor_org/${id}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(updateData),
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || "Failed to update organization");
  }

  return data;
}
// ✅ Upload org logo to a dedicated collection — returns { logo_url }
// Backend stores image in org_assets collection (or S3/Firebase) and returns URL
async function uploadOrgLogo(orgId, base64Image) {
  const response = await apiFetch(`${BASE_URL}/upload_org_logo`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ orgId, logo_data: base64Image }),
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || "Failed to upload logo");
  }

  return data.logo_url; // URL pointing to stored image
}

// ✅ Update org branding only (primary_color + logo_url) — allowed for manufacturer role
async function updateOrgBranding(orgId, { primary_color, logo }) {
  const response = await apiFetch(`${BASE_URL}/update_org_branding/${orgId}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify({ primary_color, logo }),
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || "Failed to update branding");
  }

  return data;
}

// ✅ Get all vendor organizations + direct customers for filter dropdown
async function getAllVendorOrgAndCustomers() {
  const response = await apiFetch(`${BASE_URL}/get_all_vendor_org_and_customers`, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.message || "Failed to fetch organizations and customers");
  }

  // ✅ RETURN FULL OBJECT (not data.data)
  return data; 
}

// ✅ Get organization by subdomain (public — no auth required)
async function getOrgBySubdomain(subdomain, requestOptions = {}) {
  const response = await apiFetch(`${BASE_URL}/get_org_by_subdomain/${subdomain}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  }, requestOptions);

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || "Failed to fetch organization by subdomain");
  }

  return data.data; // { primary_color, subdomain, ... }
}

 // ✅ RETURN ALL FUNCTIONS
  return {
    getAllVendorOrg,
    createVendorOrg,
    deleteVendorOrg,
    getVendorOrgById,
    updateVendorOrg,
    updateOrgBranding,
    uploadOrgLogo,
    getAllVendorOrgAndCustomers,
    getOrgBySubdomain,
  };

}
